import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatMenuModule } from "@angular/material/menu";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DATE_FORMATS, MatNativeDateModule } from "@angular/material/core";
import { MatInputModule } from "@angular/material/input";
import { TranslocoPipe } from "@jsverse/transloco";
import { combineLatest, first, forkJoin, switchMap } from "rxjs";
import {
  LorenMeteringType,
  LorenMeteringTypeTranslation,
} from "../../../loren-assets/interfaces/loren-metering-type";
import { LorenMetering } from "../../../loren-assets/interfaces/loren-metering.interface";
import {
  LorenAssetReportTypeService
} from "../loren-asset-reports/services/loren-asset-report-type-service/loren-asset-report-type.service";
import { LorenChartData } from "../../interfaces/loren-chart-data.interface";
import { LorenLineChartData } from "../../../../shared/modules/loren-line-chart/interfaces/loren-line-chart-data.interface";
import {
  LorenLineChartPoint
} from "../../../../shared/modules/loren-line-chart/interfaces/loren-line-chart-point.interface";
import {
  LorenLineChartUtilsService
} from "../../../../shared/modules/loren-line-chart/services/loren-line-chart-utils-service/loren-line-chart-utils.service";
import { LorenChartUtilsService } from "../../../../shared/services/loren-chart-utils-service/loren-chart-utils.service";
import {
  LorenLineChartComponent
} from "../../../../shared/modules/loren-line-chart/components/loren-line-chart/loren-line-chart.component";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../../shared/modules/loren-asset-card/constants/loren-metric-types.constants";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import {
  LorenFeatureToggleService
} from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";
import { clone } from "remeda";
import { RefreshSubject } from "../../../../common/rxjs/RefreshSubject";
import {
  countDays,
  DateRange,
  evaluateDateRange
} from "../../../../common/forms/period-select/period-select.component";
import { isFillLevelSensor } from "../../../asset/fill-level.utils";
import {
  LorenMeteringContext,
  METERING_CONTEXT_TRANSLATION_NAMES,
} from "../../../loren-assets/enums/loren-metering-context.enum";

const NATIVE_DATE_FORMATS = {
  parse: {
    dateInput: "LL",
  },
  display: {
    dateInput: "dd MMMM yyyy",
    monthYearLabel: "MMM yyyy",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM yyyy",
  },
};

@Component({
  imports: [
    FormsModule,
    MatCardModule,
    MatProgressSpinner,
    MatSlideToggle,
    MatMenuModule,
    TranslocoPipe,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    LorenLineChartComponent,
    ReactiveFormsModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: NATIVE_DATE_FORMATS },
  ],
  selector: "loren-numeric-line-chart-report",
  templateUrl: "./loren-numeric-line-chart-report.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./loren-numeric-line-chart-report.component.scss"],
})
export class LorenNumericLineChartReportComponent implements OnInit {
  private featureToggleService = inject(LorenFeatureToggleService);
  private assetReportTypeService = inject(LorenAssetReportTypeService);

  private readonly destroyRef = inject(DestroyRef);
  readonly binary = LorenMetricCategories.BINARY;
  readonly numeric = LorenMetricCategories.NUMERIC;
  readonly percentage = LorenMetricCategories.PERCENTAGE;

  meterings = input<LorenMetering[]>();
  private readonly meterings$ = toObservable(this.meterings);

  refresh$ = input.required<RefreshSubject>()
  type = input.required<LorenMeteringType>();
  isHandset = input<boolean>();
  context = input<LorenMetricCategories>(this.numeric);
  yMin = computed(() => {
    switch (this.context()) {
      case LorenMetricCategories.BINARY:
      case LorenMetricCategories.PERCENTAGE:
        return 0;
      default:
        if (isFillLevelSensor(this.type())) return 0;
        // Only constrain the axis to the min/max band when it is shown,
        // otherwise let the chart auto-scale to the average line.
        return this.chartsData().showMinMax ? this.chartsData().yMin : undefined;
    }
  })
  yMax = computed(() => {
    switch (this.context()) {
      case LorenMetricCategories.BINARY:
        return 1;
      case LorenMetricCategories.PERCENTAGE:
        return 100;
      default:
        if (isFillLevelSensor(this.type())) return 4;
        return this.chartsData().showMinMax ? this.chartsData().yMax : undefined;
    }
  })

  @Output() updateData =
    new EventEmitter<LorenLineChartData[]>();

  chartsData = signal<LorenChartData>(null);
  allowNotes = signal<boolean>(false);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  period = input.required<DateRange>();
  days = computed(() => {
    const [from, to] = evaluateDateRange(this.period());
    return countDays(from, to);
  });

  private readonly period$ = toObservable(this.period);

  ngOnInit() {
    this.loading.set(true);
    const meteringContext = this.meterings()?.[0]?.meteringContext;
    const chartTitle = meteringContext === LorenMeteringContext.STATE
      ? METERING_CONTEXT_TRANSLATION_NAMES.get(meteringContext)
      : LorenMeteringTypeTranslation.get(this.type());
    const binaryContext =
      this.context() === this.binary
        ? {
          binaryContext: meteringContext === LorenMeteringContext.STATE
            ? meteringContext
            : this.type()
        }
        : {};
    this.chartsData.set({
      chartTitle,
      meteringType: this.type(),
      showNotes: true,
      showMinMax: this.days() > 1,
      lineChartData: null,
      ...binaryContext,
    });
    this.featureToggleService
      .userHasFeature(LorenFeature.ADD_NOTES_TO_CHART)
      .pipe(first())
      .subscribe((res) => {
        this.allowNotes.set(res);
      });

    combineLatest([
      this.meterings$,
      this.period$,
      this.refresh$()
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([meterings, period]) => {
        return forkJoin(
          meterings.map((m) => {
            switch (LorenMetricKinds.get(m.type)) {
              case LorenMetricCategories.BINARY:
                return this.assetReportTypeService.getBinaryHistoryForMetering(
                  m.id,
                  period,
                )
              case LorenMetricCategories.PERCENTAGE:
                return this.assetReportTypeService.getNumericHistoryForMetering(
                  m.id,
                  period,
                ).pipe(
                  map(x => {
                    const mutated = clone(x);

                    for (const item of mutated.items) {
                      item.min = item.min * 100;
                      item.max = item.max * 100;
                      item.value = item.value * 100;
                    }

                    return mutated;
                  }),
                )
              default:
                return this.assetReportTypeService.getNumericHistoryForMetering(
                  m.id,
                  period
                )
            }
          })
        )
      })
    ).subscribe({
      next: (res) => {
        this.error.set(false);
        this.prepareData(res);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
        this.chartsData.set(null);
        this.updateData.emit([]);
      },
    })
  }

  reloadData() {
    this.refresh$().next();
  }

  chartHasNotes(chart: LorenChartData) {
    return chart.lineChartData?.some((item) =>
      item.series.some((i) => i.notes?.length)
    );
  }

  toggleShowMinMaxValues() {
    this.chartsData.update((c) => ({
      ...c,
      showMinMax: !this.chartsData().showMinMax,
    }));
  }

  toggleShowNotes() {
    this.chartsData.update((c) => ({
      ...c,
      showNotes: !this.chartsData().showNotes,
    }));
  }

  private prepareData(res) {
    const lineChartData: LorenLineChartData[] = res.some(
      (r) => r.items?.length
    )
      ? res.map((r, i) => {
        return this.getDataForMetering(
          this.getModel(
            r.items,
            this.meterings()[i],
            r.metricType === LorenMeteringType.DOOR_WINDOW_BINARY_SENSOR
              ? r.totalFalseState
              : null
          ).lorenLineChartData
        );
      })
      : null;

    this.chartsData.update((v) => ({
      ...v,
      lineChartData,
      ...this.getMinMaxBounds(lineChartData),
    }));
    this.updateData.emit(lineChartData);
  }

  private getMinMaxBounds(
    lineChartData: LorenLineChartData[]
  ): { yMin: number; yMax: number } {
    const mins = (lineChartData ?? [])
      .flatMap((d) => d.series.map((s) => s.min))
      .filter((v): v is number => v !== null && v !== undefined);
    const maxs = (lineChartData ?? [])
      .flatMap((d) => d.series.map((s) => s.max))
      .filter((v): v is number => v !== null && v !== undefined);

    if (!mins.length || !maxs.length) {
      return { yMin: undefined, yMax: undefined };
    }

    const min = Math.min(...mins);
    const max = Math.max(...maxs);
    // Pad the axis by 10% of the range so the band does not touch the edges.
    const offset = (max - min) * 0.1;

    return {
      // Keep a min of 0 anchored to the axis rather than padding below it.
      yMin: min === 0 ? 0 : min - offset,
      yMax: max + offset,
    };
  }

  private getModel(
    items: LorenLineChartPoint[],
    metering: LorenMetering,
    totalFalseState = null
  ) {
    const xAxisStep = LorenChartUtilsService.getXAxisSteps(
      items.length || 0,
      this.isHandset()
    );
    return LorenLineChartUtilsService.prepareLineChartViewModel(
      items,
      xAxisStep,
      this.period(),
      metering,
      totalFalseState
    );
  }

  private getDataForMetering(data: LorenLineChartData) {
    return this.context() === this.numeric
      ? this.toggleMinMaxValuesForChartData(this.days() > 1, data)
      : data;
  }

  private toggleMinMaxValuesForChartData(
    state: boolean,
    data
  ): LorenLineChartData {
    if (!state) {
      data.series = data.series.map((x) => ({
        ...x,
        min: null,
        max: null,
      }));
      return data;
    }
    return data;
  }
}
